(ns app.front.view.group.admin.add-user
  (:require [app.external-module.model :as external-model]
            [app.front.state-machine :as sm]
            [app.front.ui :as ui]
            [app.front.ui.yes-no-radio-buttons :as yes-no]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.validate :as validate :refer [field-invalid-text field-invalid?]]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [goog.functions]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(def UserAlreadyInGroup (s/pred #(not (contains? #{::user-already-in-group} %)) 'wrong-state))


(def model {:user/email      [[validate/Email :validate/wrong-email-address]]
            :user/first-name [[validate/NonBlankString :validate/blank-first-name]]
            :user/last-name  [[validate/NonBlankString :validate/blank-last-name]]
            :user/groups     [[validate/NotEmptyList :validate/empty-user-groups]]
            ::state          [[UserAlreadyInGroup :validate/user-in-authority-group-already]]})


(defn reset-db [db]
  (update db ::document
          assoc
          :db/id nil
          :user/email ""
          :user/first-name ""
          :user/last-name ""
          :user/username nil
          :user/integration-username ""
          :user/groups []
          ::user-groups nil
          ::show-errors? false
          ::state nil))


(def state-machine
  {nil                 {::init ::ready}
   ::ready             {::set-doc-value    ::client-validation
                        ::update-doc-value ::client-validation
                        ::try-submit       ::submitting}
   ::client-validation {::no-errors    ::ready
                        ::still-errors ::client-validation}
   ::submitting        {::submit-errors  ::server-errors
                        ::submit-success ::done}})


;; -- Subscriptions -----------------------------------------------------------

(rf/reg-sub
  ::document
  (fn [db [_ path f]]
    (let [doc     (::document db)
          sub-doc (if path (get-in doc path) doc)]
      (if f (f sub-doc) sub-doc))))


(rf/reg-sub
  ::state
  :<- [::document]
  (fn [document _] (get document ::state)))


(rf/reg-sub
  ::modal-open?
  :<- [::state]
  (fn [state _] (not (contains? #{nil ::done} state))))


(rf/reg-sub ::validator
  :<- [::document]
  (fn [document _]
    (validate/model-validator document model)))


(rf/reg-sub ::field-error
  :<- [::validator]
  (fn [validator [_ field]]
    (and validator
         (validator field))))


(rf/reg-sub ::field-invalid?
  (fn [[_ field]]
    [(rf/subscribe [::document [::show-errors?]])
     (rf/subscribe [::field-error field])])
  (fn [[show? error] _]
    (and show? (some? error))))


(rf/reg-sub ::field-error-text
  (fn [[_ field]]
    [(rf/subscribe [::document [::show-errors?]])
     (rf/subscribe [::field-error field])
     (rf/subscribe [:tr])])
  (fn [[show? error tr] _]
    (if show? (tr [error]) "")))


(rf/reg-sub ::viewer-group
  :<- [::document [::user-groups]]
  (fn [groups]
    (p/find-first groups acommon/viewer-group?)))


(rf/reg-sub ::permission-groups
  :<- [::document [::user-groups]]
  (fn [groups _]
    (filter acommon/write-auth-group? groups)))


(rf/reg-sub ::groups
  :<- [::document [::user-groups]]
  (fn [groups]
    (filter acommon/user-selectable-group? groups)))


;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine
                                                  [::document ::state]))

(def debug (rf/after (fn [db event]
                       (js/console.debug "=======")
                       (js/console.debug "state: " (str (-> db ::document ::state)))
                       (js/console.debug "event: " (str event)))))

(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])


(rc/reg-chain-named*
  interceptors
  ::try-submit
  (fn [{:keys [db]} [event _]]
    {:db           (update-next-state db event)
     :comm/command {:command        :group/add-user-when-not-found-in-siblings
                    :data           (-> db
                                        ::document
                                        (select-keys [:user/email
                                                      :user/first-name
                                                      :user/last-name
                                                      :user/groups])
                                        (update :user/groups #(map :db/id %))
                                        (assoc :parent-group-id (-> db ::document ::parent-group-id)))
                    :on-bad-request [::submit-failure.bad-request]
                    :on-forbidden   [::submit-failure.forbidden]}})
  ::submit-success
  (fn [{:keys [db]} [_ _ {:keys [result] :as response}]]
    {:db         (reset-db db)
     :dispatch-n (if (= :existing result)
                   [[:notifications/add
                     :toast
                     :warning
                     :warning/warning
                     :group/already-in-group]]
                   [(conj (-> db ::document :dispatch-after-submit)
                          (merge (-> db
                                     ::document
                                     (select-keys [:user/email
                                                   :user/first-name
                                                   :user/last-name
                                                   :user/groups]))
                                 {:db/id (:id response)}))
                    [:notifications/add
                     :toast
                     :success
                     :success
                     :group/new-user-added]])}))


(rc/reg-chain* ::submit-failure.bad-request
  interceptors
  (fn [{:keys [db tr]} [event status body]]
    (js/console.error "::submit-failure" status body)
    {:db       (-> db
                   (assoc-in [::document ::server-errors] (:errors body))
                   (update-next-state event))
     :dispatch [:notifications/add
                :inline
                :error
                (tr [:error/error])
                (tr [:error/bad-request])]}))


(rc/reg-chain* ::submit-failure.forbidden
  interceptors
  (fn [{:keys [db tr]} [event status body]]
    (js/console.error "::submit-failure" status body)
    {:db       (update-next-state db event)
     :dispatch [:notifications/add
                :inline
                :error
                (tr [:error/error])
                (tr [:error/forbidden])]}))


(rc/reg-chain* ::open
  interceptors
  (fn [{:keys [db]} [_ group-id _user-groups dispatch-after-submit]]
    {:db (-> db
             reset-db
             (assoc-in [::document :dispatch-after-submit] dispatch-after-submit)
             (assoc-in [::document ::parent-group-id] group-id))})
  (fn [{:keys [db]} [_ _ user-groups]]
    (when-not (empty? user-groups)
      {:db (-> db (assoc-in [::document ::user-groups] user-groups))}))
  (fn [{:keys [db]} _]
    {:db (-> db (update-next-state ::init))}))



(rf/reg-event-fx ::set-doc-value
  interceptors
  (fn [{:keys [db]} [event path value]]
    {:db       (-> db
                   (assoc-in (into [::document] path) value)
                   (update ::document dissoc ::server-errors)
                   (update-next-state event))
     :dispatch [::validate-fields]}))


(rf/reg-event-fx ::update-doc-value
  (fn [{:keys [db]} [event path f value]]
    {:db       (-> db
                   (update-in (into [::document] path) f value)
                   (update ::document dissoc ::server-errors)
                   (update-next-state event))
     :dispatch [::validate-fields]}))


(rf/reg-event-fx ::validate-fields
  interceptors
  (fn [{:keys [db tr]} _]
    {:dispatch (if (or (false? (get-in db [::document ::show-errors?]))
                       (empty? (validate/validate-form (::document db) model tr)))
                 [::no-errors]
                 [::still-errors])}))


(rf/reg-event-fx ::submit-clicked
  interceptors
  (fn [{:keys [db tr]} _]
    {:db       (assoc-in db [::document ::show-errors?] true)
     :dispatch (if-not (empty? (validate/validate-form (::document db) model tr))
                 [::validate-fields]
                 [::try-submit])}))


(rf/reg-event-fx ::close-clicked
  interceptors
  (fn [{:keys [db]} _]
    {:db (-> db reset-db)}))


(defn handle-open [group-id user-groups dispatch-after-submit]
  #(>evt [::open group-id user-groups dispatch-after-submit]))


(rf/reg-event-db ::no-errors update-next-state)
(rf/reg-event-db ::still-errors update-next-state)

(defn val->evt [path]
  (fn [e] (rf/dispatch-sync [::set-doc-value path (futil/tval e)])))


(defn select-group-fn [group write-auth-group superdomain?]
  (fn [groups]
    (if superdomain?
      ;; Superdomain: no mutual exclusion, just auto-check write-auth
      (if (and (acommon/write-group? group)
               write-auth-group)
        (conj (set groups) group write-auth-group)
        (conj groups group))
      ;; Non-superdomain: viewer/write mutual exclusion
      (cond
        (acommon/viewer-group? group) [group]
        ;; One role per external module: picking a role drops the other roles.
        (external-model/role-group? group) (->> groups
                                                (remove external-model/role-group?)
                                                (cons group))
        (and (acommon/write-group? group)
             write-auth-group) (->> (set groups)
                                    (cons write-auth-group)
                                    (remove acommon/viewer-group?)
                                    (cons group))
        :else (conj groups group)))))

(defn de-select-group-fn [group]
  (fn [groups]
    (let [new-groups (remove #(= group %) groups)]
      (if (and (acommon/org-admin-group? group)
               (not-any? acommon/org-admin-group? new-groups))
        (filter #(not (acommon/escrow-admin-group? %)) new-groups)
        new-groups))))

(defn group-group-checkboxes [write-auth-group superdomain? groups]
  (for-frag [group (->> groups
                        (sort acommon/group-sort))]
    (let [user-groups (<sub [::document [:user/groups]])
          disabled?   (and (acommon/escrow-admin-group? group)
                           (not-any? acommon/org-admin-group? user-groups))]
      (cc/checkbox {:id         (str (:db/id group))
                    :data-cy    "user-groups-checkbox"
                    :key        (str "checkbox-group-" (:db/id group))
                    :label-text (:group/name group)
                    :title      (:group/name group)
                    :checked    (<sub [::document [:user/groups] #(boolean (some (partial = group) %))])
                    :disabled   disabled?
                    :on-change  (fn [checked]
                                  (let [f (if checked
                                            (select-group-fn group write-auth-group superdomain?)
                                            (de-select-group-fn group))]
                                    (>evt [::update-doc-value [:user/groups] f])))}))))

(defn viewer-only-selection [tr viewer-group write-auth-group superdomain?]
  [:div
   [:div.mb-2.text-xs (tr [:user-management/viewer-only])]
   [yes-no/yes-no-radio-buttons tr "viewer-only"
    {:value-selected (<sub [::document [:user/groups] #(-> % first :group/roles set (contains? :group.role/viewer))])
     :on-change      (fn [checked]
                       (let [f (if checked
                                 (select-group-fn viewer-group write-auth-group superdomain?)
                                 (de-select-group-fn viewer-group))]
                         (>evt [::update-doc-value [:user/groups] f])))}]])

(defn viewer-permission-checkbox [tr viewer-group write-auth-group superdomain?]
  (let [viewer-checked? (<sub [::document [:user/groups] #(boolean (some (partial = viewer-group) %))])]
    [:div
     (cc/checkbox {:id         "viewer-permission-add-user"
                   :data-cy    "viewer-permission-checkbox"
                   :label-text (tr [:user-management/view-permission])
                   :checked    (boolean viewer-checked?)
                   :on-change  (fn [checked]
                                 (let [f (if checked
                                           (select-group-fn viewer-group write-auth-group superdomain?)
                                           (de-select-group-fn viewer-group))]
                                   (>evt [::update-doc-value [:user/groups] f])))})]))

(defn user-groups-checkboxes
  "`:service-account?` leaves out an external module's groups, which are for
  people only."
  ([tr] (user-groups-checkboxes tr nil))
  ([tr {:keys [service-account?]}]
   (let [validator        (<sub [::validator])
         invalid?         (field-invalid? (<sub [::document [::show-errors?]]) validator :user/groups)
         permissions      (<sub [::permission-groups])
         groups           (cond->> (<sub [::groups])
                            service-account? (remove external-model/external-user-group?))
         write-auth-group (p/find-first permissions acommon/write-auth-group?)
         viewer-group     (<sub [::viewer-group])
         viewer-only?     (<sub [::document [:user/groups] #(-> % first :group/roles set (contains? :group.role/viewer))])
         superdomain?     (<sub [::gas/superdomain-group?])
         checkboxes       (partial group-group-checkboxes write-auth-group superdomain?)]
     [:div
      [:div.bx--form-item.pb-2
       (if superdomain?
         ;; Superdomain: viewer checkbox + always show permissions & groups
         [:div.required-input
          [:div.mt-2 [:label.text-xs (tr [:user-management/permissions])]
           (when viewer-group
             [viewer-permission-checkbox tr viewer-group write-auth-group superdomain?])
           (when (seq permissions)
             (checkboxes permissions))]
          [:div.mt-2
           {:id               "user-groups-checkboxes"
            :data-invalid     (when invalid? invalid?)
            :aria-invalid     (when invalid? invalid?)
            :aria-describedby (when invalid? "user-groups-checkboxes-error-msg")}
           (when (seq groups) [:div [:label.bx--label (tr [:user-management/groups])]
                               (checkboxes groups)])]
          [:div.bx--form-requirement (field-invalid-text invalid? validator :user/groups tr)]]
         ;; Non-superdomain: viewer-only radio + hide permissions/groups when viewer-only
         [:<>
          (when viewer-group
            [viewer-only-selection tr viewer-group write-auth-group superdomain?])
          (when-not viewer-only?
            [:div.required-input
             [:div.mt-2 [:label.text-xs (tr [:user-management/permissions])]
              (when (seq permissions)
                (checkboxes permissions))]
             [:div.mt-2
              {:id               "user-groups-checkboxes"
               :data-invalid     (when invalid? invalid?)
               :aria-invalid     (when invalid? invalid?)
               :aria-describedby (when invalid? "user-groups-checkboxes-error-msg")}
              (when (seq groups) [:div [:label.bx--label (tr [:user-management/groups])]
                                  (checkboxes groups)])]
             [:div.bx--form-requirement (field-invalid-text invalid? validator :user/groups tr)]])])]])))


(defn errors-list [tr]
  [:div.flex.flex-col.items-start.mb-2
   (when (<sub [::field-invalid? ::state])
     [:ul
      [:li.text-red
       [cc/icon {:icon        i/remove
                 :description (tr [:error/error])
                 :fill        "currentcolor"
                 :class-name  "mr-2"
                 :height      "14"
                 :width       "14"}]
       (<sub [::field-error-text ::state])]])])

(defn input [{:keys [class field label-text id type auto-focus]}]
  [:div.bx--col-xs-12.my-4
   (util/mmap class)
   [ui/text-input {:id           id
                   :data-cy      id
                   :type         (or type :text)
                   :label-text   label-text
                   :light        false
                   :auto-focus   auto-focus
                   :required     true
                   :invalid      (<sub [::field-invalid? field])
                   :invalid-text (<sub [::field-error-text field])
                   :value        (<sub [::document [field]])
                   :on-change    (val->evt [field])}]])


(defn modal [tr]
  (let [open? (<sub [::modal-open?])]
    (cc/modal
      {:open                   open?
       :primary-button-text    (tr [:add])
       :secondary-button-text  (tr [:cancel])
       :modal-heading          (tr [:user-management/add])
       :on-request-close       #(>evt [::close-clicked])
       :on-request-submit      #(>evt [::submit-clicked])
       :on-secondary-submit    #(>evt [::close-clicked])
       :selector-primary-focus "#input-user-emails"}
      (when open?
        [:div.flex.flex-col
         [:p.bx--modal-content__text.mb-6
          (tr [:user/add-user-help])]
         [:form
          [:fieldset.bx--grid
           [:div.bx--row
            [input {:field      :user/email
                    :label-text (tr [:user-management/email])
                    :id         "input-user-emails"
                    :type       :email
                    :auto-focus true}]
            [input {:field      :user/first-name
                    :class      "bx--col-xxl-6"
                    :label-text (tr [:user-management/first-name])
                    :id         "input-user-first-name"}]
            [input {:field      :user/last-name
                    :class      "bx--col-xxl-6"
                    :label-text (tr [:user-management/last-name])
                    :id         "input-user-last-name"}]]
           [:div.bx--row
            [:div.bx--col-xs-12
             (cc/form-group
               {:legend-text ""}
               [user-groups-checkboxes tr])]]]
          [errors-list tr]]]))))
